import {
  Card,
  ReviewRecord,
  ProgressStatistics,
  SectionStatistics,
  SessionStatistics,
  Rating,
} from '../types/models';

/**
 * StatisticsService
 * 
 * Calculates various statistics for tracking learning progress, including:
 * - Overall progress metrics
 * - Section-specific statistics
 * - Retention rates
 * - Session summaries
 * 
 * Requirements: 5.2, 5.3, 5.4, 5.5, 3.5
 */
export class StatisticsService {
  /**
   * Calculate overall progress statistics
   * 
   * @param cards - All cards in the system
   * @param reviews - All review records
   * @returns ProgressStatistics with overall metrics
   * 
   * Requirements: 5.2, 5.3, 5.5, 3.5
   */
  calculateProgressStats(
    cards: Card[],
    reviews: ReviewRecord[]
  ): ProgressStatistics {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);

    // Total cards in the system
    const totalCards = cards.length;

    // Cards due today (nextReviewDate <= now)
    const cardsDueToday = cards.filter(
      card => card.schedule.nextReviewDate <= now
    ).length;

    // Cards reviewed today
    const reviewsToday = reviews.filter(
      review => review.reviewDate >= todayStart && review.reviewDate < todayEnd
    );
    const cardsReviewedToday = new Set(reviewsToday.map(r => r.cardId)).size;

    // Total reviews
    const totalReviews = reviews.length;

    // Retention rate (percentage of successful reviews)
    const retentionRate = this.calculateRetentionRate(reviews, Infinity);

    // Average ease factor across all cards
    const averageEaseFactor = totalCards > 0
      ? cards.reduce((sum, card) => sum + card.schedule.easeFactor, 0) / totalCards
      : 2.5;

    // Calculate streak days
    const streakDays = this.calculateStreakDays(reviews);

    return {
      totalCards,
      cardsDueToday,
      cardsReviewedToday,
      totalReviews,
      retentionRate,
      averageEaseFactor,
      streakDays,
    };
  }

  /**
   * Calculate statistics for a specific section
   * 
   * @param sectionId - ID of the section
   * @param cards - All cards in the system
   * @param reviews - All review records
   * @returns SectionStatistics for the specified section
   * 
   * Requirements: 5.4
   */
  calculateSectionStats(
    sectionId: string,
    cards: Card[],
    reviews: ReviewRecord[]
  ): SectionStatistics {
    // Filter cards and reviews for this section
    const sectionCards = cards.filter(card => card.sectionId === sectionId);
    const sectionReviews = reviews.filter(review => review.sectionId === sectionId);

    const now = new Date();

    // Total cards in section
    const totalCards = sectionCards.length;

    // Cards due for review
    const cardsDue = sectionCards.filter(
      card => card.schedule.nextReviewDate <= now
    ).length;

    // Average interval across all cards in section
    const averageInterval = totalCards > 0
      ? sectionCards.reduce((sum, card) => sum + card.schedule.interval, 0) / totalCards
      : 0;

    // Retention rate for this section
    const retentionRate = this.calculateRetentionRate(sectionReviews, Infinity);

    // Last review date for this section
    const lastReviewDate = sectionReviews.length > 0
      ? sectionReviews.reduce((latest, review) => 
          review.reviewDate > latest ? review.reviewDate : latest,
          sectionReviews[0].reviewDate
        )
      : null;

    // Get section name from first card (assuming all cards have same section)
    const sectionName = sectionCards.length > 0
      ? sectionCards[0].sectionId // Placeholder - should be fetched from Section object
      : sectionId;

    return {
      sectionId,
      sectionName,
      totalCards,
      cardsDue,
      averageInterval,
      retentionRate,
      lastReviewDate,
    };
  }

  /**
   * Calculate retention rate (success rate) from review records
   * 
   * @param reviews - Review records to analyze
   * @param timeWindow - Time window in days (Infinity for all time)
   * @returns Retention rate as a percentage (0-100)
   * 
   * Requirements: 5.5
   */
  calculateRetentionRate(reviews: ReviewRecord[], timeWindow: number): number {
    if (reviews.length === 0) {
      return 0;
    }

    // Filter reviews within time window
    const now = new Date();
    const windowStart = new Date(now);
    windowStart.setDate(windowStart.getDate() - timeWindow);

    const relevantReviews = timeWindow === Infinity
      ? reviews
      : reviews.filter(review => review.reviewDate >= windowStart);

    if (relevantReviews.length === 0) {
      return 0;
    }

    // Count successful reviews (good or easy)
    const successfulReviews = relevantReviews.filter(
      review => review.rating === 'good' || review.rating === 'easy'
    ).length;

    // Calculate percentage
    return (successfulReviews / relevantReviews.length) * 100;
  }

  /**
   * Calculate session statistics from session review records
   * 
   * @param sessionReviews - Review records from a single session
   * @returns SessionStatistics with session summary
   * 
   * Requirements: 3.5
   */
  calculateSessionStats(sessionReviews: ReviewRecord[]): SessionStatistics {
    const cardsReviewed = sessionReviews.length;

    // Calculate session duration
    let duration = 0;
    if (sessionReviews.length > 0) {
      const firstReview = sessionReviews[0].reviewDate;
      const lastReview = sessionReviews[sessionReviews.length - 1].reviewDate;
      duration = lastReview.getTime() - firstReview.getTime();
    }

    // Count ratings
    const ratings: Record<Rating, number> = {
      again: 0,
      hard: 0,
      good: 0,
      easy: 0,
      tomorrow: 0,
    };

    sessionReviews.forEach(review => {
      ratings[review.rating]++;
    });

    // Calculate average time per card
    const totalTimeSpent = sessionReviews.reduce(
      (sum, review) => sum + review.timeSpent,
      0
    );
    const averageTimePerCard = cardsReviewed > 0
      ? totalTimeSpent / cardsReviewed
      : 0;

    return {
      cardsReviewed,
      duration,
      ratings,
      averageTimePerCard,
    };
  }

  /**
   * Calculate the current streak of consecutive days with reviews
   * 
   * @param reviews - All review records
   * @returns Number of consecutive days with at least one review
   * 
   * @private
   */
  private calculateStreakDays(reviews: ReviewRecord[]): number {
    if (reviews.length === 0) {
      return 0;
    }

    // Sort reviews by date (most recent first)
    const sortedReviews = [...reviews].sort(
      (a, b) => b.reviewDate.getTime() - a.reviewDate.getTime()
    );

    // Get unique review dates (normalized to day)
    const reviewDates = new Set<string>();
    sortedReviews.forEach(review => {
      const dateStr = this.normalizeDate(review.reviewDate);
      reviewDates.add(dateStr);
    });

    const uniqueDates = Array.from(reviewDates).sort().reverse();

    // Check if there's a review today or yesterday
    const today = this.normalizeDate(new Date());
    const yesterday = this.normalizeDate(
      new Date(Date.now() - 24 * 60 * 60 * 1000)
    );

    if (uniqueDates[0] !== today && uniqueDates[0] !== yesterday) {
      return 0; // Streak broken
    }

    // Count consecutive days
    let streak = 0;
    let currentDate = new Date();

    for (let i = 0; i < uniqueDates.length; i++) {
      const expectedDate = this.normalizeDate(currentDate);
      
      if (uniqueDates[i] === expectedDate) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  }

  /**
   * Normalize a date to YYYY-MM-DD format for comparison
   * 
   * @param date - Date to normalize
   * @returns Date string in YYYY-MM-DD format
   * 
   * @private
   */
  private normalizeDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
